// Utility to transform frontend citation config data to backend schema

// Map frontend categories to backend mainCategory enum values
const CATEGORY_TO_BACKEND = {
	'Web Content': 'Web Content',
	'Social Media': 'Social & Personal Communication',
	'Personal Communication': 'Social & Personal Communication',
	Periodicals: 'Periodicals',
	Multimedia: 'Multimedia',
	'Government & Institutional Publications': 'Reports & Government Docs',
	'Data & Software': 'Data & Software',
	'Course & Education': 'Conferences & Presentations',
	'Book & Chapter': 'Books & Chapters'
};

// Reverse mapping: backend to frontend categories
const BACKEND_TO_CATEGORY = {
	'Web Content': 'Web Content',
	'Social & Personal Communication': 'Social Media', // Default to Social Media
	Periodicals: 'Periodicals',
	Multimedia: 'Multimedia',
	'Reports & Government Docs': 'Government & Institutional Publications',
	'Data & Software': 'Data & Software',
	'Conferences & Presentations': 'Course & Education',
	'Books & Chapters': 'Book & Chapter'
};

// Map frontend sources to backend subcategory enum values
const SOURCE_TO_BACKEND = {
	'Blog Post': 'Blog Post',
	Webpage: 'Webpage',
	'X/Twitter Post': 'Social Media Post',
	'Personal Communication': 'Personal Communication',
	'Journal Article': 'Journal Article',
	'Magazine Article (Print & Online)': 'Magazine Article', // CHANGED: Maps to specific subcategory
	'Newspaper Article (Print & Online)': 'Newspaper Article', // CHANGED: Maps to specific subcategory
	Preprint: 'Preprint',
	'Film/Movie': 'Film/Movie',
	'YouTube Video': 'YouTube Video',
	'TV Series Episode': 'TV Episode',
	'Podcast Episode': 'Podcast Episode',
	'Webinar/Lecture (recorded & retrievable)': 'Webinar/Lecture',
	'Webinar/Lecture': 'Webinar/Lecture',
	'Song/Track': 'Song or Music Track',
	'Infographic/Chart/Graph': 'Infographic or Figure',
	'Radio Broadcast': 'Radio Broadcast',
	'Press Release': 'Report',
	'Institutional Report': 'Report',
	'Data set': 'Dataset or Software',
	'Course Handout': 'Course Handout',
	'Conference Presentation': 'Conference Presentation',
	'Open Educational Resource': 'Open Educational Resource',
	'Conference Session': 'Conference Session',
	'Conference Presentation / Session': 'Conference Presentation',
	Book: 'Book',
	'Chapter in Edited Book': 'Chapter in an Edited Book',
	'Reference Work Entry': 'Reference Work Entry',
	'Thesis/Dissertation': 'Report'
};
// Reverse mapping: backend subcategory to frontend source type (for citation generator)
const BACKEND_TO_SOURCE = {
	'Blog Post': 'Blog Post',
	Webpage: 'Webpage',
	'Social Media Post': 'X/Twitter Post',
	'Personal Communication': 'Personal Communication',
	'Journal Article': 'Journal Article',
	'Magazine Article': 'Magazine Article (Print & Online)', // ADDED
	'Newspaper Article': 'Newspaper Article (Print & Online)', // ADDED
	'Magazine or Newspaper Article': 'Magazine Article (Print & Online)', // Fallback for old data
	Preprint: 'Preprint',
	'Film/Movie': 'Film/Movie',
	'YouTube Video': 'YouTube Video',
	'Webinar/Lecture': 'Webinar/Lecture',
	'TV Episode': 'TV Series Episode',
	'Podcast Episode': 'Podcast Episode',
	'Radio Broadcast': 'Radio Broadcast',
	'Song or Music Track': 'Song/Track',
	'Infographic or Figure': 'Infographic/Chart/Graph',
	Report: 'Press Release', // Default to Press Release
	'Dataset or Software': 'Data set',
	'Conference Presentation': 'Conference Presentation / Session',
	'Conference Session': 'Conference Presentation / Session',
	'Course Handout': 'Course Handout',
	'Open Educational Resource': 'Open Educational Resource',
	Book: 'Book',
	'Chapter in an Edited Book': 'Chapter in Edited Book',
	'Reference Work Entry': 'Reference Work Entry'
};

// Parse author string into contributor object (NEW SIMPLIFIED FORMAT)
function parseAuthorName(fullName) {
	if (!fullName || typeof fullName !== 'string') return null;

	const trimmed = fullName.trim();
	if (!trimmed) return null;

	// Backend now expects simple { name: String } format
	return { name: trimmed };
}

// Transform list of author strings to contributor array
function transformAuthors(authorList) {
	if (!Array.isArray(authorList)) return [];
	return authorList.map(parseAuthorName).filter((author) => author !== null);
}

/** Maps unified conference UI to legacy backend subcategories (descriptor-driven). */
export function resolveConferenceUnifiedSubcategory(formData) {
	const raw = (formData.descriptor || '').trim().toLowerCase();
	const sessionDescriptors = new Set([
		'conference session',
		'symposium',
		'panel discussion',
		'poster presentation',
		'keynote address',
		'workshop'
	]);
	if (sessionDescriptors.has(raw)) return 'Conference Session';
	return 'Conference Presentation';
}

// Main transformation function
export function mapFrontendToBackend(category, source, formData, documentId) {
	let subcategory = SOURCE_TO_BACKEND[source] || source;
	if (source === 'Conference Presentation / Session') {
		subcategory = resolveConferenceUnifiedSubcategory(formData);
	}

	const backendData = {
		mainCategory: CATEGORY_TO_BACKEND[category] || category,
		subcategory,
		document: documentId
	};

	// Determine which mode is selected: Author(s) or Organization
	const authorOrOrgSelection = formData.AuthorOrOrganization;

	// Process each form field
	Object.entries(formData).forEach(([fieldName, value]) => {
		// Skip undefined or null, but ALLOW empty strings (so fields can be cleared on edit)
		if (value === undefined || value === null) return;
		// Skip empty arrays for non-critical fields
		if (
			Array.isArray(value) &&
			value.length === 0 &&
			fieldName !== 'presentersOrganization' &&
			fieldName !== 'presentersLecturers' &&
			fieldName !== 'contributorsOrganization' &&
			fieldName !== 'author' &&
			fieldName !== 'authors'
		) {
			return;
		}

		if (fieldName === 'accessMethod') {
			if (value) backendData.context = value;
			return;
		}

		// Skip conditional metadata fields and internal fields
		if (
			fieldName === 'director' ||
			fieldName === 'AuthorOrOrganization' ||
			fieldName === 'license' || // legacy alias of notes — not a form field
			(fieldName === 'format' && subcategory !== 'Course Handout') ||
			fieldName === '_selectedSource' ||
			fieldName === '_selectedCategory' ||
			// Skip backend-only fields that shouldn't be sent in updates
			fieldName === '_id' ||
			fieldName === 'id' ||
			fieldName === 'createdAt' ||
			fieldName === 'updatedAt' ||
			fieldName === 'createdBy' ||
			fieldName === 'isDeleted' ||
			// Skip frontend display fields
			fieldName === 'sourceType' ||
			fieldName === 'category' ||
			fieldName === 'title' || // Generic title - specific titles like titleOfArticle are handled separately
			fieldName === 'presenters' || // presenters array from backend - handled by presentersOrganization, etc.
			fieldName === 'editor' || // Skip 'editor' field as it's handled by 'editors'
			fieldName === 'descriptorCustom' // Merged into descriptor for OER
		) {
			return;
		}

		// Handle author fields
		if (fieldName === 'author' || fieldName === 'authors') {
			// Process authors if:
			// 1. "Author(s)" is explicitly selected (citations with AuthorOrOrganization radio)
			// 2. No AuthorOrOrganization radio exists (citations with direct author field like Book, Thesis)
			if (authorOrOrgSelection === 'Author(s)' || !authorOrOrgSelection) {
				const authors = transformAuthors(Array.isArray(value) ? value : [value]);
				// Always set authors when processing, even if empty
				// Backend will validate if it's required for the specific subcategory
				backendData.authors = authors;
			}
			return;
		}

		// Handle organization field - ONLY if "Organization" is selected
		if (fieldName === 'organization') {
			// Only process organization if the user selected "Organization"
			if (authorOrOrgSelection === 'Organization') {
				// Always set organization when Organization is selected, even if empty
				// Backend will validate if it's required for the specific subcategory
				backendData.organization = value || '';
			}
			return;
		}

		// Handle titleOfPost specially - maps to different backend fields based on subcategory
		if (fieldName === 'titleOfPost') {
			if (subcategory === 'Social Media Post') {
				backendData.excerptOrPostTitle = value;
			} else {
				backendData.title = value;
			}
			return;
		}

		// Handle titleOfBook specially - maps to different backend fields based on subcategory
		if (fieldName === 'titleOfBook') {
			if (subcategory === 'Chapter in an Edited Book' || subcategory === 'Reference Work Entry') {
				backendData.bookTitle = value;
			} else {
				backendData.title = value;
			}
			return;
		}

		// Handle pageRange fields - combine start and end into object
		if (fieldName === 'pageRangeStart' || fieldName === 'pageRangeEnd') {
			// Store these temporarily, we'll combine them later
			return;
		}

		// Handle pageNumbers fields - combine start and end into formatted string
		if (fieldName === 'pageNumbersStart' || fieldName === 'pageNumbersEnd') {
			// Store these temporarily, we'll combine them later
			return;
		}

		// Handle contributorsOrganization for Radio Broadcast separately
		if (fieldName === 'contributorsOrganization' && subcategory === 'Radio Broadcast') {
			const contributors = transformAuthors(Array.isArray(value) ? value : [value]);
			backendData.contributors = contributors;
			return;
		}

		// Handle presenters for Conference Presentation AND Conference Session
		if (
			fieldName === 'presentersOrganization' ||
			fieldName === 'presentersLecturers' ||
			fieldName === 'presenterOrganization' ||
			fieldName === 'contributorsOrganization' // Conference Session also needs presenters
		) {
			const presenters = transformAuthors(Array.isArray(value) ? value : [value]);
			// Always set presenters field for Conference Presentation and Webinar/Lecture
			// even if empty, so backend validation can properly handle it
			backendData.presenters = presenters;
			return;
		}

		// Handle other contributor types (Film/Movie, etc.)
		if (fieldName === 'contributors') {
			const contributors = transformAuthors(Array.isArray(value) ? value : [value]);
			if (contributors.length > 0) {
				// Film/Movie uses 'creators' field on backend
				if (subcategory === 'Film/Movie') {
					backendData.creators = contributors;
				} else {
					backendData.contributors = contributors;
				}
			}
			return;
		}

		if (fieldName === 'writers') {
			const writers = transformAuthors(Array.isArray(value) ? value : [value]);
			if (writers.length > 0) {
				backendData.writers = writers;
			}
			return;
		}

		if (fieldName === 'directors') {
			const directors = transformAuthors(Array.isArray(value) ? value : [value]);
			if (directors.length > 0) {
				backendData.directors = directors;
			}
			return;
		}

		if (
			fieldName === 'editors' ||
			fieldName === 'referenceWorkEditors' ||
			fieldName === 'entryEditors'
		) {
			const editors = transformAuthors(Array.isArray(value) ? value : [value]);
			if (editors.length > 0) {
				backendData.editors = editors;
			}
			return;
		}

		if (fieldName === 'chapterAuthors' || fieldName === 'entryAuthors') {
			const authors = transformAuthors(Array.isArray(value) ? value : [value]);
			if (authors.length > 0) {
				backendData.authors = authors;
			}
			return;
		}

		if (fieldName === 'artistGroup') {
			const artists = transformAuthors(Array.isArray(value) ? value : [value]);
			if (artists.length > 0) {
				backendData.artists = artists;
			}
			return;
		}

		// Handle authorUploader specially for YouTube Video and Webinar/Lecture
		if (fieldName === 'authorUploader') {
			// YouTube Video and Webinar/Lecture subcategories require uploaderOrAuthor as a string
			if (subcategory === 'YouTube Video' || subcategory === 'Webinar/Lecture') {
				// Take the first author/uploader
				const firstAuthor = Array.isArray(value) ? value[0] : value;
				if (firstAuthor) {
					backendData.uploaderOrAuthor = firstAuthor;
				}
			}
			return;
		}

		if (fieldName === 'executiveProducers') {
			const creators = transformAuthors(Array.isArray(value) ? value : [value]);
			if (creators.length > 0) {
				backendData.creators = creators;
			}
			return;
		}

		// Handle platformPublisher specially for OER - maps to publisher
		if (fieldName === 'platformPublisher' && subcategory === 'Open Educational Resource') {
			backendData.publisher = value;
			return;
		}

		// Field mappings - comprehensive mapping for all citation types
		const fieldMappings = {
			// Title fields
			// Note: titleOfPost and titleOfBook are handled specially above based on subcategory
			titleOfWebPage: 'title',
			titleOfVideo: 'title',
			titleOfArticle: 'articleTitle',
			titleOfManuscript: 'title',
			titleOfWork: 'title',
			titleOfEpisode: 'episodeTitle',
			titleOfSong: 'trackTitle',
			// titleOfBook: handled specially above
			titleOfChapter: 'chapterTitle',
			titleOfEntry: 'entryTitle',
			titleOfPaper: 'title',
			titleOfReport: 'title',
			titleOfDataset: 'title',
			titleOfOer: 'title',
			titleOfPresentation: 'title',
			titleOfPressRelease: 'title',
			titleOfRecording: 'title',
			titleOfThesisDissertation: 'title',
			titleOfJournal: 'journalTitle',
			titleOfMagazine: 'publicationName',
			titleOfNewspaper: 'publicationName',
			titleOfSeries: 'seriesTitle',
			titleOfPodcast: 'podcastTitle',
			titleOfAlbum: 'albumTitle',
			titleOfReferenceWork: 'referenceWorkTitle',

			// Blog Post uses blogName (stored as websiteName in backend)
			blogName: 'websiteName',
			// Webpage uses websiteName
			websiteName: 'websiteName',

			// Single contributor/name fields
			authorCommunicator: 'communicatorName',
			uploaderOrAuthor: 'uploaderOrAuthor',
			authorName: 'authorName',

			// Social media fields
			excerptOrPostTitle: 'excerptOrPostTitle',
			handleUsername: 'authorHandle',

			// URLs and identifiers
			doiUrl: 'doi',
			url: 'url',
			slidesUrl: 'slidesUrl',

			// Date fields
			date: 'date',
			year: 'year',
			airDate: 'airDate',
			retrievalDate: 'retrievalDate',

			// Publisher/Platform variations
			publisher: 'publisher',
			publisherNetwork: 'publisher',
			publisherRepository: 'publisher',
			websitePublisher: 'publisher',
			recordLabel: 'publisherOrLabel',
			publisherOrPlatform: 'publisherOrPlatform',
			productionCompany: 'productionCompany',
			platform: 'platform',
			platformName: 'platform',
			platformPublisher: 'publisherOrPlatform',
			websitePlatformName: 'platform',

			// Repository and location
			repositoryArchiveName: 'repositoryName',
			nameOfConference: 'conferenceName',
			eventInstitution: 'conferenceName',
			conferenceDetails: 'conferenceName',
			location: 'location',
			locationOfConference: 'location',
			institution: 'location',

			// Publication details
			descriptor: 'descriptor',
			format: 'descriptor',
			pageNumbers: 'pageNumbers',
			volume: 'volume',
			volumes: 'volume',
			issue: 'issue',
			edition: 'edition',
			version: 'version',
			versionEdition: 'version',
			entryId: 'entryId',

			// TV/Media specific
			season: 'season',
			episodeNumber: 'episodeNumber',
			duration: 'duration',
			timestamp: 'duration',

			publiclyAccessible: 'publiclyAccessible',

			// Communication fields
			typeOfCommunication: 'communicationType',
			recipient: 'recipient',

			// Context and sections
			medium: 'context',
			institutionContext: 'context',
			department: 'context',
			presenterRoles: 'context',
			contributorRoles: 'context',
			course: 'sectionName',

			// Other fields
			reportNumber: 'reportNumber',
			publicationNumberAccessionId: 'reportNumber',
			notes: 'notes',
			license: 'notes',
			originalWorkPublishedYear: 'notes'
		};

		const backendField = fieldMappings[fieldName] || fieldName;

		// Handle contributorRoles specially (convert array to string)
		if (fieldName === 'contributorRoles' && Array.isArray(value)) {
			// Filter out empty entries and join with comma
			const nonEmptyRoles = value.filter((r) => r && typeof r === 'string' && r.trim() !== '');
			if (nonEmptyRoles.length > 0) {
				backendData.context = nonEmptyRoles.join(', ');
			}
			return;
		}

		// Handle presenterRoles specially (convert array to string)
		if (fieldName === 'presenterRoles' && Array.isArray(value)) {
			// Filter out empty entries and join with comma
			const nonEmptyRoles = value.filter((r) => r && typeof r === 'string' && r.trim() !== '');
			if (nonEmptyRoles.length > 0) {
				backendData.context = nonEmptyRoles.join(', ');
			}
			return;
		}

		// Handle translator specially (convert array to string)
		// Handle both 'translators' (plural) and 'translator' (singular) field names
		if ((fieldName === 'translators' || fieldName === 'translator') && Array.isArray(value)) {
			// Filter out empty/whitespace-only entries before joining
			const nonEmptyTranslators = value.filter(
				(t) => t && typeof t === 'string' && t.trim() !== ''
			);
			if (nonEmptyTranslators.length > 0) {
				backendData.translator = nonEmptyTranslators.join(', ');
			}
			// If all translators are empty, don't set the field at all
			return;
		}

		// Special handling for date field - TV Episode uses airDate
		if (fieldName === 'date' && subcategory === 'TV Episode') {
			backendData.airDate = value;
			return;
		}

		if (fieldName === 'issue') {
			backendData.issue = value != null && value !== '' ? String(value) : null;
			return;
		}

		// Set the mapped field
		// Convert empty strings to null so backend knows to clear the field
		backendData[backendField] = value === '' ? null : value;
	});

	// Handle page range - combine start and end into object
	if (formData.pageRangeStart && formData.pageRangeEnd) {
		backendData.pageRange = {
			start: formData.pageRangeStart,
			end: formData.pageRangeEnd
		};
	}

	// Handle page numbers - combine start and end into a formatted string
	if (formData.pageNumbersStart || formData.pageNumbersEnd) {
		if (formData.pageNumbersStart && formData.pageNumbersEnd) {
			// Both provided: format as "A1–A5"
			backendData.pageNumbers = `${formData.pageNumbersStart}–${formData.pageNumbersEnd}`;
		} else if (formData.pageNumbersStart) {
			// Only start provided
			backendData.pageNumbers = formData.pageNumbersStart;
		} else if (formData.pageNumbersEnd) {
			// Only end provided (unusual but handle it)
			backendData.pageNumbers = formData.pageNumbersEnd;
		}
	}

	// Special handling for Webinar/Lecture, Infographic, and Conference types - doiUrl should populate both doi and url
	if (
		(subcategory === 'Webinar/Lecture' ||
			subcategory === 'Infographic or Figure' ||
			subcategory === 'Conference Presentation' ||
			subcategory === 'Conference Session') &&
		formData.doiUrl
	) {
		// Map doiUrl to doi field
		backendData.doi = formData.doiUrl;
		// Also map to url field (required for these subcategories)
		if (!backendData.url) {
			backendData.url = formData.doiUrl;
		}
	}

	// Special handling for web content citations - REMOVED
	// Previously this code set organization = first author name for Blog Post/Webpage
	// This caused issues with edit mode showing wrong selection
	// The backend should handle authors OR organization separately

	// Special handling for social media posts
	if (subcategory === 'Social Media Post') {
		// Ensure authorName is set (required by backend)
		if (!backendData.authorName) {
			if (backendData.authors && backendData.authors.length > 0) {
				backendData.authorName = backendData.authors[0].name;
			} else if (backendData.organization) {
				backendData.authorName = backendData.organization;
			}
		}
	}

	// Extract year from date if year is not provided
	// Required for: Book, Chapter in an Edited Book, Reference Work Entry, Journal Article,
	// Preprint, Report, Dataset or Software, Film/Movie, Song or Music Track
	const yearRequiredSubcategories = [
		'Book',
		'Chapter in an Edited Book',
		'Reference Work Entry',
		'Journal Article',
		'Preprint',
		'Report',
		'Dataset or Software',
		'Film/Movie',
		'Song or Music Track',
		'YouTube Video',
		'Webinar/Lecture'
	];

	if (yearRequiredSubcategories.includes(subcategory)) {
		if (!backendData.year && backendData.date) {
			// Extract 4-digit year from date string
			const yearMatch = backendData.date.match(/\b(19|20)\d{2}\b/);
			if (yearMatch) {
				backendData.year = yearMatch[0];
			}
		}
	}

	// FINAL SAFEGUARD: Enforce mutual exclusivity between authors and organization
	// (except for special cases like web content where organization is derived from authors)
	if (authorOrOrgSelection === 'Author(s)') {
		// If Author(s) selected, remove organization UNLESS it's derived from authors (web content)
		if (subcategory !== 'Blog Post' && subcategory !== 'Webpage') {
			delete backendData.organization;
		}
	} else if (authorOrOrgSelection === 'Organization') {
		// If Organization selected, remove authors
		delete backendData.authors;
	}

	if (source === 'Open Educational Resource') {
		const custom = (formData.descriptorCustom || '').trim();
		if (formData.descriptor === 'Other' && custom) {
			backendData.descriptor = custom;
		}
	}

	return backendData;
}

/**
 * Get the frontend source type name that the citation generator expects
 * @param {string} backendSubcategory - Backend subcategory value
 * @param {object} citationData - Full citation object (optional, used for disambiguation)
 * @returns {string} - Frontend source type for citation generator
 */
/** Resolve frontend category from a backend citation (Personal Communication vs Social Media, etc.). */
export function getFrontendCategory(backendCitation) {
	if (!backendCitation) return '';
	if (backendCitation.category && !backendCitation.mainCategory) {
		return backendCitation.category;
	}
	let frontendCategory =
		BACKEND_TO_CATEGORY[backendCitation.mainCategory] || backendCitation.mainCategory || '';
	if (backendCitation.mainCategory === 'Social & Personal Communication') {
		frontendCategory =
			backendCitation.subcategory === 'Personal Communication'
				? 'Personal Communication'
				: 'Social Media';
	}
	return frontendCategory;
}

export function getFrontendSourceType(backendSubcategory, citationData = null) {
	// Legacy backend subcategory: infer newspaper vs magazine from URL host when possible
	if (backendSubcategory === 'Magazine or Newspaper Article' && citationData) {
		const url = String(citationData.doi || citationData.url || '').toLowerCase();
		const newsHints = [
			'nytimes.com',
			'washingtonpost.com',
			'theguardian.com',
			'wsj.com',
			'chronicle.com',
			'bbc.com',
			'reuters.com',
			'bostonglobe.com',
			'latimes.com',
			'chicagotribune.com',
			'usatoday.com'
		];
		if (newsHints.some((h) => url.includes(h))) {
			return 'Newspaper Article (Print & Online)';
		}
	}

	// Special handling for "Report" - distinguish between Thesis/Dissertation, Institutional Report, and Press Release
	if (backendSubcategory === 'Report' && citationData) {
		const descriptor = citationData.descriptor || '';

		// Check if it's a Thesis/Dissertation based on descriptor
		if (
			descriptor.toLowerCase().includes('thesis') ||
			descriptor.toLowerCase().includes('dissertation')
		) {
			return 'Thesis/Dissertation';
		}

		// Check if it's a Press Release based on descriptor
		if (descriptor.toLowerCase().includes('press release')) {
			return 'Press Release';
		}

		// Default to Institutional Report for other Report types
		return 'Institutional Report';
	}

	// Otherwise, use the default mapping
	return BACKEND_TO_SOURCE[backendSubcategory] || backendSubcategory;
}

// Transform backend citation to frontend display format
export function mapBackendToFrontend(backendCitation) {
	// Helper to extract author name from contributor object
	const extractAuthorName = (author) => {
		if (!author) return '';
		if (typeof author === 'string') return author;
		// New schema: contributors have just { name: String }
		if (author.name) return author.name;
		// Fallback for any old data
		if (author.fullName) return author.fullName;
		if (author.organization) return author.organization;
		const name = `${author.firstName || ''} ${author.lastName || ''}`.trim();
		return name || 'Unknown Author';
	};

	// Helper to extract year from date string
	const extractYear = (dateString) => {
		if (!dateString) return '';
		const yearMatch = dateString.match(/\b(19|20)\d{2}\b/);
		return yearMatch ? yearMatch[0] : '';
	};

	// Extract transformed fields - handle different contributor types
	let transformedAuthors = [];

	// Determine authors for display — prefer individual authors over organization when both exist
	if (
		backendCitation.authors &&
		Array.isArray(backendCitation.authors) &&
		backendCitation.authors.length > 0 &&
		backendCitation.authors.some((a) => extractAuthorName(a)?.trim())
	) {
		transformedAuthors = backendCitation.authors.map(extractAuthorName);
	} else if (backendCitation.organization) {
		transformedAuthors = [backendCitation.organization];
	} else if (backendCitation.communicatorName) {
		transformedAuthors = [backendCitation.communicatorName];
	} else if (backendCitation.uploaderOrAuthor) {
		transformedAuthors = [backendCitation.uploaderOrAuthor];
	}
	// Handle authorName (Social Media)
	else if (backendCitation.authorName) {
		transformedAuthors = [backendCitation.authorName];
	}
	// Handle other contributor arrays
	else if (backendCitation.editors && backendCitation.editors.length > 0) {
		transformedAuthors = backendCitation.editors.map(extractAuthorName);
	} else if (backendCitation.directors && backendCitation.directors.length > 0) {
		transformedAuthors = backendCitation.directors.map(extractAuthorName);
	} else if (backendCitation.creators && backendCitation.creators.length > 0) {
		transformedAuthors = backendCitation.creators.map(extractAuthorName);
	} else if (backendCitation.artists && backendCitation.artists.length > 0) {
		transformedAuthors = backendCitation.artists.map(extractAuthorName);
	} else if (backendCitation.presenters && backendCitation.presenters.length > 0) {
		transformedAuthors = backendCitation.presenters.map(extractAuthorName);
	} else if (backendCitation.contributors && backendCitation.contributors.length > 0) {
		transformedAuthors = backendCitation.contributors.map(extractAuthorName);
	} else if (backendCitation.writers && backendCitation.writers.length > 0) {
		transformedAuthors = backendCitation.writers.map(extractAuthorName);
	}

	const frontendCategory = getFrontendCategory(backendCitation);

	// Extract title based on citation type
	let displayTitle =
		backendCitation.title ||
		backendCitation.chapterTitle ||
		backendCitation.entryTitle ||
		backendCitation.articleTitle ||
		backendCitation.episodeTitle ||
		backendCitation.trackTitle ||
		backendCitation.excerptOrPostTitle ||
		backendCitation.titleOfManuscript; // Add explicit check for titleOfManuscript if needed, though usually mapped to title

	// Special handling for Personal Communication - use communicationType as title
	if (backendCitation.subcategory === 'Personal Communication') {
		if (backendCitation.communicationType) {
			displayTitle = backendCitation.communicationType;
		} else {
			displayTitle = 'Personal communication';
		}
	}

	return {
		...backendCitation, // Spread first, then override with transformed fields
		id: backendCitation._id,
		_id: backendCitation._id,
		subcategory: backendCitation.subcategory, // Keep backend subcategory for reference
		sourceType: getFrontendSourceType(backendCitation.subcategory, backendCitation), // Convert to frontend-friendly name
		category: frontendCategory, // Convert backend category to frontend category
		authors: transformedAuthors, // Override the object array with string array
		year: backendCitation.year || extractYear(backendCitation.date),
		title: displayTitle
	};
}

/**
 * Maps backend citation fields to frontend form fields for editing
 * @param {object} backendCitation - Citation object from backend
 * @returns {object} - Form data for the edit modal
 */
export function mapBackendToFormFields(backendCitation) {
	const formData = {
		// Only include essential backend fields that don't conflict with form field mappings
		// DO NOT spread all backend fields as it causes duplicate field issues
		_id: backendCitation._id,
		id: backendCitation.id
	};

	// Context-aware mappings based on subcategory
	const subcategory = backendCitation.subcategory || '';

	// Map title field based on subcategory
	// Map title field based on subcategory
	if (backendCitation.title) {
		if (subcategory === 'Blog Post') {
			formData.titleOfPost = backendCitation.title;
		} else if (subcategory === 'Webpage') {
			formData.titleOfWebPage = backendCitation.title;
		} else if (subcategory === 'YouTube Video') {
			formData.titleOfVideo = backendCitation.title;
		} else if (subcategory === 'Webinar/Lecture') {
			formData.titleOfRecording = backendCitation.title;
			formData.title = backendCitation.title;
		} else if (subcategory === 'Book') {
			formData.titleOfBook = backendCitation.title;
		} else if (subcategory === 'Report') {
			// Check descriptor to determine if it's Thesis/Dissertation or regular Report
			const descriptor = backendCitation.descriptor || '';
			if (
				descriptor.toLowerCase().includes('thesis') ||
				descriptor.toLowerCase().includes('dissertation')
			) {
				formData.titleOfThesisDissertation = backendCitation.title;
			} else if (descriptor.toLowerCase().includes('press release')) {
				formData.titleOfPressRelease = backendCitation.title;
			} else {
				formData.titleOfReport = backendCitation.title;
			}
		} else if (subcategory === 'Dataset or Software') {
			formData.titleOfDataset = backendCitation.title;
		} else if (subcategory === 'Open Educational Resource') {
			formData.titleOfOer = backendCitation.title;
		} else if (subcategory === 'Conference Presentation' || subcategory === 'Conference Session' || subcategory === 'Course Handout') {
			formData.titleOfPresentation = backendCitation.title;
		} else if (subcategory === 'Preprint') {
			formData.titleOfManuscript = backendCitation.title;
		} else if (subcategory === 'Film/Movie') {
			formData.titleOfWork = backendCitation.title;
		} else if (subcategory === 'Infographic or Figure') {
			formData.titleOfWork = backendCitation.title;
		} else {
			// Default fallback
			formData.titleOfWork = backendCitation.title;
		}
	}

	// Handle Social Media Post title (excerptOrPostTitle → titleOfPost)
	if (subcategory === 'Social Media Post' && backendCitation.excerptOrPostTitle) {
		formData.titleOfPost = backendCitation.excerptOrPostTitle;
	}

	// All other specific field mappings
	// Title variations
	if (backendCitation.articleTitle) formData.titleOfArticle = backendCitation.articleTitle;
	if (backendCitation.chapterTitle) formData.titleOfChapter = backendCitation.chapterTitle;
	if (backendCitation.entryTitle) formData.titleOfEntry = backendCitation.entryTitle;
	if (backendCitation.episodeTitle) formData.titleOfEpisode = backendCitation.episodeTitle;
	if (backendCitation.trackTitle) formData.titleOfSong = backendCitation.trackTitle;
	if (backendCitation.bookTitle) formData.titleOfBook = backendCitation.bookTitle;
	if (backendCitation.journalTitle) formData.titleOfJournal = backendCitation.journalTitle;
	if (backendCitation.publicationName) {
		if (subcategory === 'Newspaper Article') {
			formData.titleOfNewspaper = backendCitation.publicationName;
		} else if (subcategory === 'Magazine Article') {
			formData.titleOfMagazine = backendCitation.publicationName;
		} else {
			// Legacy or ambiguous: populate both so generator + edit form still resolve
			formData.titleOfMagazine = backendCitation.publicationName;
			formData.titleOfNewspaper = backendCitation.publicationName;
		}
	}
	if (backendCitation.seriesTitle) formData.titleOfSeries = backendCitation.seriesTitle;
	if (backendCitation.podcastTitle) formData.titleOfPodcast = backendCitation.podcastTitle;
	if (backendCitation.albumTitle) formData.titleOfAlbum = backendCitation.albumTitle;
	if (backendCitation.referenceWorkTitle)
		formData.titleOfReferenceWork = backendCitation.referenceWorkTitle;
	if (backendCitation.excerptOrPostTitle)
		formData.excerptOrPostTitle = backendCitation.excerptOrPostTitle;

	// Website/Blog name - map based on subcategory
	if (backendCitation.websiteName) {
		if (subcategory === 'Blog Post') {
			formData.blogName = backendCitation.websiteName;
		} else {
			formData.websiteName = backendCitation.websiteName;
		}
	}

	// Conference name
	if (backendCitation.conferenceName) {
		formData.nameOfConference = backendCitation.conferenceName;
		formData.eventInstitution = backendCitation.conferenceName;
		formData.conferenceDetails = backendCitation.conferenceName;
	}

	// Single contributor/name fields
	if (backendCitation.communicatorName)
		formData.authorCommunicator = backendCitation.communicatorName;
	if (backendCitation.uploaderOrAuthor) {
		// For YouTube Video, map uploaderOrAuthor back to authorUploader array
		if (backendCitation.subcategory === 'YouTube Video') {
			formData.authorUploader = [backendCitation.uploaderOrAuthor];
		} else {
			formData.uploaderOrAuthor = backendCitation.uploaderOrAuthor;
		}
	}
	if (backendCitation.authorHandle) formData.handleUsername = backendCitation.authorHandle;
	if (backendCitation.authorName) formData.authorName = backendCitation.authorName;
	if (backendCitation.recipient) formData.recipient = backendCitation.recipient;

	// URLs and identifiers
	if (backendCitation.doi) formData.doiUrl = backendCitation.doi;
	if (backendCitation.url) {
		formData.url = backendCitation.url;
		if (
			!formData.doiUrl &&
			(subcategory === 'Conference Presentation' ||
				subcategory === 'Conference Session' ||
				subcategory === 'Report')
		) {
			formData.doiUrl = backendCitation.url;
		}
	}
	if (backendCitation.slidesUrl) formData.slidesUrl = backendCitation.slidesUrl;

	// Date fields
	if (backendCitation.airDate) {
		formData.airDate = backendCitation.airDate;
		// TV Episode: airDate maps to date field in the form
		if (subcategory === 'TV Episode') {
			formData.date = backendCitation.airDate;
		}
	}
	if (backendCitation.retrievalDate) formData.retrievalDate = backendCitation.retrievalDate;
	if (backendCitation.date) formData.date = backendCitation.date;
	if (backendCitation.year) formData.year = backendCitation.year;

	// Publisher variations
	if (backendCitation.publisher) {
		formData.publisher = backendCitation.publisher;
		formData.publisherNetwork = backendCitation.publisher;
		formData.publisherRepository = backendCitation.publisher;
		formData.websitePublisher = backendCitation.publisher;
		// OER uses platformPublisher
		if (subcategory === 'Open Educational Resource') {
			formData.platformPublisher = backendCitation.publisher;
		}
	}
	if (backendCitation.publisherOrLabel) formData.recordLabel = backendCitation.publisherOrLabel;
	if (backendCitation.publisherOrPlatform) {
		formData.publisherOrPlatform = backendCitation.publisherOrPlatform;
		formData.platformPublisher = backendCitation.publisherOrPlatform;
	}
	if (backendCitation.productionCompany)
		formData.productionCompany = backendCitation.productionCompany;

	// Platform variations
	if (backendCitation.platform) {
		formData.platform = backendCitation.platform;
		formData.platformName = backendCitation.platform;
		formData.websitePlatformName = backendCitation.platform;
	}

	// Repository and location
	if (backendCitation.repositoryName)
		formData.repositoryArchiveName = backendCitation.repositoryName;
	if (backendCitation.location) {
		formData.location = backendCitation.location;
		formData.locationOfConference = backendCitation.location;
		formData.institution = backendCitation.location;
	}

	// Publication details
	if (backendCitation.descriptor) {
		formData.descriptor = backendCitation.descriptor;
		if (subcategory === 'Course Handout') {
			formData.format = backendCitation.descriptor;
		}
	}
	if (subcategory === 'Open Educational Resource' && formData.descriptor) {
		const presets = new Set([
			'Textbook',
			'Course materials',
			'Learning module',
			'Video',
			'Syllabus',
			'Lecture slides',
			'Other'
		]);
		if (!presets.has(formData.descriptor)) {
			formData.descriptorCustom = formData.descriptor;
			formData.descriptor = 'Other';
		}
	}
	if (backendCitation.pageRange) {
		formData.pageRangeStart = backendCitation.pageRange.start;
		formData.pageRangeEnd = backendCitation.pageRange.end;
		// CRITICAL: Also create formatted pageRange string for generators
		// Generators expect data.pageRange as a string like "15–18"
		if (backendCitation.pageRange.start && backendCitation.pageRange.end) {
			formData.pageRange = `${backendCitation.pageRange.start}–${backendCitation.pageRange.end}`;
		}
	}
	if (backendCitation.pageNumbers) {
		formData.pageNumbers = backendCitation.pageNumbers;
		// Split pageNumbers into start and end if it contains a dash/en-dash
		const pageNumbersMatch = backendCitation.pageNumbers.match(/^(.+?)[–-](.+)$/);
		if (pageNumbersMatch) {
			formData.pageNumbersStart = pageNumbersMatch[1].trim();
			formData.pageNumbersEnd = pageNumbersMatch[2].trim();
		} else {
			// Single page or no range
			formData.pageNumbersStart = backendCitation.pageNumbers;
			formData.pageNumbersEnd = '';
		}
	}
	if (backendCitation.volume) {
		formData.volume = backendCitation.volume;
		formData.volumes = backendCitation.volume;
	}
	if (backendCitation.issue != null && backendCitation.issue !== '') {
		formData.issue = String(backendCitation.issue);
	}
	if (backendCitation.edition) formData.edition = backendCitation.edition;
	if (backendCitation.version) {
		formData.version = backendCitation.version;
		// formData.versionEdition = backendCitation.version;

		// DO NOT set versionEdition - it creates duplicate fields that overwrite each other
	}
	if (backendCitation.entryId) formData.entryId = backendCitation.entryId;

	// TV/Media specific fields
	if (backendCitation.season) formData.season = backendCitation.season;
	if (backendCitation.episodeNumber) formData.episodeNumber = backendCitation.episodeNumber;
	if (backendCitation.duration) {
		formData.duration = backendCitation.duration;
		formData.timestamp = backendCitation.duration;
	}

	// Communication fields
	if (backendCitation.communicationType)
		formData.typeOfCommunication = backendCitation.communicationType;

	// Context and sections — subcategory-specific (context is overloaded in backend)
	if (backendCitation.context) {
		if (subcategory === 'Course Handout') {
			formData.department = backendCitation.context;
		} else if (subcategory === 'Song or Music Track') {
			formData.accessMethod = backendCitation.context;
			formData.medium = backendCitation.context;
		} else if (
			subcategory === 'Radio Broadcast' ||
			subcategory === 'Film/Movie' ||
			subcategory === 'Podcast Episode' ||
			subcategory === 'Conference Presentation' ||
			subcategory === 'Conference Session'
		) {
			if (typeof backendCitation.context === 'string') {
				const roles = backendCitation.context.split(',').map((r) => r.trim());
				formData.contributorRoles = roles;
				formData.presenterRoles = roles;
			} else {
				formData.contributorRoles = backendCitation.context;
				formData.presenterRoles = backendCitation.context;
			}
		} else {
			formData.medium = backendCitation.context;
			formData.institutionContext = backendCitation.context;
		}
	}
	if (backendCitation.sectionName) formData.course = backendCitation.sectionName;

	// Other fields
	if (backendCitation.reportNumber) {
		formData.reportNumber = backendCitation.reportNumber;
		formData.publicationNumberAccessionId = backendCitation.reportNumber;
	}
	if (backendCitation.notes) {
		formData.notes = backendCitation.notes;
	}
	if (backendCitation.publiclyAccessible !== undefined && backendCitation.publiclyAccessible !== null) {
		formData.publiclyAccessible = backendCitation.publiclyAccessible;
	}
	if (backendCitation.groupAuthorAbbreviation) {
		formData.groupAuthorAbbreviation = backendCitation.groupAuthorAbbreviation;
	}

	// Helper to extract author name from contributor object
	const extractAuthorName = (author) => {
		if (!author) return '';
		if (typeof author === 'string') return author;
		if (author.name) return author.name;
		if (author.fullName) return author.fullName;
		if (author.organization) return author.organization;
		const name = `${author.firstName || ''} ${author.lastName || ''}`.trim();
		return name || 'Unknown Author';
	};

	// MUTUAL EXCLUSIVITY: Handle Authors OR Organization (never both)
	// Prefer authors when both exist — backend sometimes stores organization alongside author names
	const hasAuthorRecords =
		backendCitation.authors &&
		Array.isArray(backendCitation.authors) &&
		backendCitation.authors.length > 0 &&
		backendCitation.authors.some((a) => {
			const n = extractAuthorName(a);
			return n && String(n).trim();
		});

	if (hasAuthorRecords) {
		const authorList = backendCitation.authors.map(extractAuthorName);
		formData.author = authorList;
		formData.AuthorOrOrganization = 'Author(s)';
		formData.organization = undefined;
		formData.authors = undefined;

		if (subcategory === 'Social Media Post' && authorList[0]) {
			formData.authorName = authorList[0];
		}

		if (subcategory === 'Book') {
			formData.authors = authorList;
		} else if (subcategory === 'Report') {
			const descriptor = backendCitation.descriptor || '';
			if (
				descriptor.toLowerCase().includes('thesis') ||
				descriptor.toLowerCase().includes('dissertation')
			) {
				formData.authors = authorList;
			}
		} else if (subcategory === 'Chapter in an Edited Book') {
			formData.chapterAuthors = authorList;
		} else if (subcategory === 'Reference Work Entry') {
			formData.entryAuthors = authorList;
		}
	} else if (backendCitation.organization) {
		formData.organization = backendCitation.organization;
		formData.AuthorOrOrganization = 'Organization';
		formData.author = undefined;
		formData.authors = undefined;
		if (subcategory === 'Social Media Post') {
			formData.authorName = backendCitation.organization;
		}
	}

	// Special handling for editors
	if (backendCitation.editors && Array.isArray(backendCitation.editors)) {
		formData.editor = backendCitation.editors.map(extractAuthorName);
		formData.editors = backendCitation.editors.map(extractAuthorName); // For generators
	}

	// Special handling for directors
	if (backendCitation.directors && Array.isArray(backendCitation.directors)) {
		formData.director = backendCitation.directors.map(extractAuthorName);
		formData.directors = backendCitation.directors.map(extractAuthorName); // For TV Episode generator
	}

	// Special handling for artists
	if (backendCitation.artists && Array.isArray(backendCitation.artists)) {
		formData.artistGroup = backendCitation.artists.map(extractAuthorName); // For Song/Track generator
	}

	// Special handling for creators
	if (backendCitation.creators && Array.isArray(backendCitation.creators)) {
		if (subcategory === 'Film/Movie') {
			// Film/Movie: creators map back to contributors
			formData.contributors = backendCitation.creators.map(extractAuthorName);
			// Also map roles for Film/Movie
			if (backendCitation.context) {
				if (typeof backendCitation.context === 'string') {
					formData.contributorRoles = backendCitation.context.split(',').map((r) => r.trim());
				} else {
					formData.contributorRoles = backendCitation.context;
				}
			}
		} else if (subcategory === 'TV Episode') {
			formData.executiveProducers = backendCitation.creators.map(extractAuthorName);
		}
	}

	// Special handling for writers (TV Episodes)
	if (backendCitation.writers && Array.isArray(backendCitation.writers)) {
		formData.writers = backendCitation.writers.map(extractAuthorName); // For TV Episode generator
	}

	// Special handling for contributors (Films, Podcasts, Radio)
	// Film/Movie uses `creators` as canonical; an empty `contributors` array must not wipe mapped names.
	if (backendCitation.contributors && Array.isArray(backendCitation.contributors)) {
		const skipForFilmCreators =
			subcategory === 'Film/Movie' &&
			Array.isArray(backendCitation.creators) &&
			backendCitation.creators.length > 0;

		if (!skipForFilmCreators) {
			const contributorList = backendCitation.contributors.map(extractAuthorName);

			if (subcategory === 'Radio Broadcast') {
				formData.contributorsOrganization = contributorList;
			} else {
				formData.contributors = contributorList;
			}

			// Map roles if available - split if string, otherwise use as-is
			if (backendCitation.context && subcategory !== 'Course Handout' && subcategory !== 'Song or Music Track') {
				if (typeof backendCitation.context === 'string') {
					formData.contributorRoles = backendCitation.context.split(',').map((r) => r.trim());
				} else {
					formData.contributorRoles = backendCitation.context;
				}
			}
		}
	}

	// Special handling for presenters (Conference Presentation, Conference Session, Webinar/Lecture)
	if (backendCitation.presenters && Array.isArray(backendCitation.presenters)) {
		const presenterList = backendCitation.presenters.map(extractAuthorName);

		if (
			backendCitation.subcategory === 'Conference Presentation' ||
			subcategory === 'Conference Presentation' ||
			subcategory === 'Conference Session'
		) {
			formData.presentersOrganization = presenterList;
			formData.contributorsOrganization = presenterList;
		} else if (subcategory === 'Webinar/Lecture') {
			formData.presentersLecturers = presenterList;
		} else if (subcategory === 'Course Handout') {
			formData.presenterOrganization = presenterList;
		} else {
			formData.presentersOrganization = presenterList;
			formData.presentersLecturers = presenterList;
			formData.contributorsOrganization = presenterList;
		}

		if (backendCitation.context) {
			const roles =
				typeof backendCitation.context === 'string'
					? backendCitation.context.split(',').map((r) => r.trim())
					: backendCitation.context;
			formData.contributorRoles = roles;
			formData.presenterRoles = roles;
		}
	}

	// Special handling for Course Handout presenter
	if (backendCitation.subcategory === 'Course Handout' && formData.author) {
		formData.presenterOrganization = formData.author; // generateCourseHandout expects data.presenterOrganization
	}

	// Special handling for translators (backend stores as string, frontend as array)
	if (backendCitation.translator && typeof backendCitation.translator === 'string') {
		formData.translators = backendCitation.translator.split(',').map((t) => t.trim());
	} else if (backendCitation.translators && Array.isArray(backendCitation.translators)) {
		formData.translators = backendCitation.translators.map(extractAuthorName);
	}

	// Special handling for reference work editors
	if (backendCitation.referenceWorkEditors && Array.isArray(backendCitation.referenceWorkEditors)) {
		formData.referenceWorkEditors = backendCitation.referenceWorkEditors.map(extractAuthorName);
	}

	// Special handling for entry editors (for revised entries)
	if (backendCitation.entryEditors && Array.isArray(backendCitation.entryEditors)) {
		formData.entryEditors = backendCitation.entryEditors.map(extractAuthorName);
	}

	// Special handling for Thesis/Dissertation - ensure correct field names for citation generator
	if (backendCitation.subcategory === 'Report') {
		const descriptor = backendCitation.descriptor || '';
		if (
			descriptor.toLowerCase().includes('thesis') ||
			descriptor.toLowerCase().includes('dissertation')
		) {
			// Ensure these fields are set for proper citation generation
			if (backendCitation.location) {
				formData.institution = backendCitation.location;
			}
			if (backendCitation.publisher) {
				formData.publisherRepository = backendCitation.publisher;
			}
			if (backendCitation.reportNumber) {
				formData.publicationNumberAccessionId = backendCitation.reportNumber;
			}
			if (backendCitation.publicationStatus) {
				formData.publicationStatus = backendCitation.publicationStatus;
			} else {
				formData.publicationStatus = 'Database';
			}
		}
	}

	// CRITICAL: Ensure 'author' field is set for in-text citations
	// generateInText checks data.author first, so we need to populate it from specialized fields
	// if it's not already set (for citation types that use specialized author fields)
	if ((!formData.author || formData.author.length === 0) && !formData.organization) {
		// Try to populate author from specialized fields for in-text citation generation
		if (formData.authorUploader && formData.authorUploader.length > 0) {
			formData.author = formData.authorUploader;
			if (!formData.AuthorOrOrganization) formData.AuthorOrOrganization = 'Author(s)';
		} else if (formData.presentersOrganization && formData.presentersOrganization.length > 0) {
			formData.author = formData.presentersOrganization;
			if (!formData.AuthorOrOrganization) formData.AuthorOrOrganization = 'Author(s)';
		} else if (formData.presentersLecturers && formData.presentersLecturers.length > 0) {
			formData.author = formData.presentersLecturers;
			if (!formData.AuthorOrOrganization) formData.AuthorOrOrganization = 'Author(s)';
		} else if (formData.contributorsOrganization && formData.contributorsOrganization.length > 0) {
			formData.author = formData.contributorsOrganization;
			if (!formData.AuthorOrOrganization) formData.AuthorOrOrganization = 'Author(s)';
		} else if (formData.presenterOrganization && formData.presenterOrganization.length > 0) {
			formData.author = formData.presenterOrganization;
			if (!formData.AuthorOrOrganization) formData.AuthorOrOrganization = 'Author(s)';
		} else if (formData.artistGroup && formData.artistGroup.length > 0) {
			formData.author = formData.artistGroup;
			if (!formData.AuthorOrOrganization) formData.AuthorOrOrganization = 'Author(s)';
		} else if (formData.contributors && formData.contributors.length > 0) {
			formData.author = formData.contributors;
			if (!formData.AuthorOrOrganization) formData.AuthorOrOrganization = 'Author(s)';
		} else if (formData.writers && formData.writers.length > 0) {
			formData.author = formData.writers;
			if (!formData.AuthorOrOrganization) formData.AuthorOrOrganization = 'Author(s)';
		} else if (formData.directors && formData.directors.length > 0) {
			formData.author = formData.directors;
			if (!formData.AuthorOrOrganization) formData.AuthorOrOrganization = 'Author(s)';
		} else if (formData.chapterAuthors && formData.chapterAuthors.length > 0) {
			formData.author = formData.chapterAuthors;
			if (!formData.AuthorOrOrganization) formData.AuthorOrOrganization = 'Author(s)';
		} else if (formData.entryAuthors && formData.entryAuthors.length > 0) {
			formData.author = formData.entryAuthors;
			if (!formData.AuthorOrOrganization) formData.AuthorOrOrganization = 'Author(s)';
		} else if (formData.authors && formData.authors.length > 0) {
			formData.author = formData.authors;
			if (!formData.AuthorOrOrganization) formData.AuthorOrOrganization = 'Author(s)';
		}
	}

	if (['Magazine Article', 'Newspaper Article', 'Preprint'].includes(backendCitation.subcategory)) {
		formData.publishedOnline = backendCitation.publishedOnline || 'Yes';
	}

	return formData;
}
